import { useCallback, useState } from "react"
import { Link } from "react-router-dom"
import { Button } from "@/components/Button"
import { Input } from "@/components/Form/Input"
import { ConfirmDialog } from "@/components/Dialog/ConfirmDialog"
import { useToast } from "@/components/Toast"
import {
  useUnbackedAccess,
  useCompTeamSubscription,
  type UnbackedAccessTeam,
} from "@/hooks/useTeamSubscription"
import { APIError } from "@/api"
import { buildCompPayload, compExpiryIsInThePast } from "@/lib/compPayload"
import { ExclamationTriangleIcon, GiftIcon } from "@heroicons/react/24/outline"

function formatExpiry(expiresAt: string | null) {
  if (!expiresAt) return "never"
  const d = new Date(expiresAt)
  return Number.isNaN(d.getTime()) ? expiresAt : d.toLocaleDateString()
}

/**
 * The state of a team as of RIGHT NOW, re-read immediately before a comp.
 *
 * `unknown` is deliberately not folded into `backed`: "we could not check" and
 * "there is nothing to do" call for opposite actions, and this panel already
 * exists because a failed check that looks like an all-clear is the dangerous
 * shape.
 */
type RevalidateResult =
  | { status: "still-unbacked"; team: UnbackedAccessTeam }
  | { status: "backed" }
  | { status: "unknown"; message: string }

type Revalidate = (teamPublicId: string) => Promise<RevalidateResult>

/**
 * One team, plus the inline reason field that turns its hand-granted plan into a
 * real Stripe comp.
 *
 * Plan and expiry are deliberately READ-ONLY, mirroring the team's existing
 * access row. This panel exists to make an existing grant real, not to change
 * its terms — a tier or date change is a separate commercial decision, and
 * doing it here would be invisible to the customer. To change terms, revoke the
 * comp on the team page and grant a new one.
 */
function UnbackedTeamRow({ team, revalidate }: { team: UnbackedAccessTeam; revalidate: Revalidate }) {
  const { toast } = useToast()
  const compMutation = useCompTeamSubscription()
  const [expanded, setExpanded] = useState(false)
  const [reason, setReason] = useState("")
  const [checking, setChecking] = useState(false)
  // The row the admin actually confirmed, captured at confirm time. The payload
  // is built from THIS, not from the `team` prop, so what the dialog described
  // is exactly what gets sent.
  const [confirmTeam, setConfirmTeam] = useState<UnbackedAccessTeam | null>(null)

  const busy = checking || compMutation.isPending
  // Since #1463 the backend drops expired grants from this worklist, so a fresh
  // response never contains one. This catches the row that expired while the
  // page sat open — no polling, no refetch-on-focus — where the cached row still
  // looks compable and the comp endpoint would refuse its date. Show the row,
  // withhold the action.
  const expiredGrant = compExpiryIsInThePast(team.expires_at)

  /**
   * Re-read this team and decide whether a comp may still proceed. Returns the
   * fresh row, or null having already explained why not.
   *
   * Why re-read at all: this page can sit open for hours — the admin QueryClient
   * sets `refetchOnWindowFocus: false` and this query does not poll — while a
   * checkout, a webhook, or another admin gives the team a real billing row or
   * changes its grant. `POST /admin/subscriptions/comp` does not require the team
   * to still be unbacked (its only guard is one-live-COMP-per-team, which a paid
   * subscription does not trip), so a click on a stale row creates a second,
   * redundant 100%-off Stripe subscription on a team that just started paying —
   * undoable only by hunting it down in the Stripe dashboard.
   *
   * `expected` is the row the admin confirmed, when there is one. A comp must
   * send exactly the terms that were on screen when they said yes, so terms that
   * moved underneath are a stop, not a silent substitution.
   */
  const checkStillCompable = async (
    expected: UnbackedAccessTeam | null
  ): Promise<UnbackedAccessTeam | null> => {
    setChecking(true)
    let check: RevalidateResult
    try {
      check = await revalidate(team.team_public_id)
    } finally {
      setChecking(false)
    }

    if (check.status === "unknown") {
      toast({
        title: "Could not re-check this team",
        description: `${check.message}. Not comping — creating one against terms we cannot confirm means a live 100%-off subscription to find and cancel by hand.`,
        variant: "destructive",
      })
      return null
    }

    if (check.status === "backed") {
      toast({
        title: `${team.team_name} no longer needs a comp`,
        // Two ways off this worklist, and from here they are indistinguishable —
        // the row is simply absent. Naming only the billing-row case would state
        // as fact the one we cannot tell apart from the other (#1463).
        description:
          "It is not holding unbacked access any more — either a billing row backs it now, or its grant expired. Check the team page for which.",
      })
      return null
    }

    // Belt and braces. The backend already excludes expired grants from this
    // list, so a row that reaches here with a past date means it expired in the
    // moments between the server's query and this response.
    if (compExpiryIsInThePast(check.team.expires_at)) {
      toast({
        title: "This grant has already expired",
        description: `It ended ${formatExpiry(check.team.expires_at)}, and a comp cannot carry a past date. Settle it on the team page.`,
        variant: "destructive",
      })
      return null
    }

    if (
      expected &&
      (check.team.plan_id !== expected.plan_id || check.team.expires_at !== expected.expires_at)
    ) {
      toast({
        title: `${team.team_name}'s terms changed`,
        description: `It is now "${check.team.plan_id}" ${
          check.team.expires_at
            ? `expiring ${formatExpiry(check.team.expires_at)}`
            : "with no end date"
        }, not what you confirmed. Nothing was comped — check the updated row and decide again.`,
        variant: "destructive",
      })
      return null
    }

    return check.team
  }

  // Validate BEFORE opening the dialog, so a missing reason is an inline
  // correction rather than a confirmation prompt that then fails. Same order as
  // the team page's comp flow.
  const onCompRequested = async () => {
    if (!reason.trim()) {
      toast({
        title: "Reason required",
        description: "The reason is what marks this subscription as a comp in Stripe and the DB.",
        variant: "destructive",
      })
      return
    }

    // Checked here too, not only at confirm, for two reasons the confirm-time
    // check cannot serve: don't raise a prompt for something already moot, and
    // put the CURRENT terms in the dialog so what the admin approves is what
    // gets sent.
    const fresh = await checkStillCompable(null)
    if (!fresh) return
    setConfirmTeam(fresh)
  }

  const onComp = async () => {
    // Only ever the confirmed row: no fallback to the prop, or a revalidation
    // that found different terms would be silently discarded here.
    const target = confirmTeam
    if (!target) return

    // Check AGAIN, at the last moment before the Stripe write. The pre-dialog
    // check is not enough on its own: a confirmation dialog has no time limit,
    // so an admin can open it and answer it an hour later, by which point the
    // team may have bought a real subscription. What remains after this is the
    // refetch-to-mutate gap, which only a precondition inside the comp
    // transaction can close.
    const fresh = await checkStillCompable(target)
    if (!fresh) return

    try {
      const result = await compMutation.mutateAsync(buildCompPayload(fresh, reason))
      toast({
        title: `${fresh.team_name} comped`,
        description: `Created ${result.stripeSubscriptionId} in Stripe at 100% off.`,
      })
      // No manual refetch: the mutation invalidates ["admin", "subscriptions"],
      // which prefix-matches this panel's query key, so the row drops out on its
      // own once the team has a granting billing row.
    } catch (e) {
      toast({
        title: "Comp failed",
        description: e instanceof APIError ? e.originalError.message : "Request failed",
        variant: "destructive",
      })
    }
  }

  return (
    <div className="py-3 border-b border-border last:border-b-0">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <Link
            to={`/teams/${team.team_public_id}`}
            className="font-medium text-primary hover:text-blue-600 hover:underline"
          >
            {team.team_name}
          </Link>
          <div className="text-xs text-secondary mt-0.5">
            <span className="font-mono">{team.team_public_id}</span>
            <span className="mx-2">·</span>
            plan <span className="font-medium">{team.plan_id}</span>
            <span className="mx-2">·</span>
            {expiredGrant ? (
              <span className="text-red-600 font-medium">expired {formatExpiry(team.expires_at)}</span>
            ) : (
              <>expires {formatExpiry(team.expires_at)}</>
            )}
          </div>
        </div>
        {!expiredGrant && !expanded && (
          <Button size="sm" onClick={() => setExpanded(true)}>
            <GiftIcon className="h-4 w-4 mr-1.5" />
            Comp
          </Button>
        )}
      </div>

      {expiredGrant && (
        <p className="text-xs text-secondary mt-2 pl-1">
          This grant's end date has passed, so it cannot be comped — the comp endpoint refuses a past
          expiry, and this panel copies a team's terms rather than choosing new ones. The team is on
          the free tier now: the date is enforced, so the grant stopped granting when it ran out. If
          it should not have, re-grant it with a future date on the{" "}
          <Link to={`/teams/${team.team_public_id}`} className="text-primary hover:underline">
            team page
          </Link>{" "}
          and then comp it.
        </p>
      )}

      {expanded && (
        <div className="mt-3 pl-1 space-y-2">
          <Input
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this team comped? (required — recorded on the subscription and in the audit log)"
            disabled={busy}
          />
          <p className="text-xs text-secondary">
            Creates a real Stripe subscription on <span className="font-medium">{team.plan_id}</span> at
            100% off
            {team.expires_at
              ? `, cancelling ${formatExpiry(team.expires_at)}.`
              : ", with no end date."}{" "}
            Access does not change — this gives the existing grant a billing row behind it.
          </p>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={onCompRequested} disabled={busy}>
              {compMutation.isPending ? "Comping…" : checking ? "Re-checking…" : "Create comp in Stripe"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setExpanded(false)
                setReason("")
              }}
              disabled={busy}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmTeam !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmTeam(null)
        }}
        title={`Comp ${(confirmTeam ?? team).team_name}?`}
        description={`Creates a real Stripe subscription for "${(confirmTeam ?? team).plan_id}" at 100% off${
          (confirmTeam ?? team).expires_at
            ? `, ending ${formatExpiry((confirmTeam ?? team).expires_at)}`
            : ", with no end date"
        }. The team's access is then derived from it.`}
        confirmLabel="Create comp"
        onConfirm={onComp}
      />
    </div>
  )
}

/**
 * Teams with access that nothing in Stripe backs.
 *
 * Renders nothing when the list is empty, so an all-clear is silence rather than
 * a permanent empty box — the panel appearing at all is the signal.
 */
export function UnbackedAccessPanel() {
  const { data, isLoading, error, refetch } = useUnbackedAccess()

  // Re-read the worklist on demand, for a row about to be comped. Lives here
  // rather than in the row so every row shares the one query rather than each
  // holding its own observer.
  const revalidate = useCallback<Revalidate>(
    async (teamPublicId) => {
      const res = await refetch()
      // `isError` first: on a failed refetch TanStack keeps the last GOOD `data`,
      // so reading `data` alone would quietly re-approve the stale row — the exact
      // failure this check exists to prevent.
      if (res.isError || !res.data) {
        const message =
          res.error instanceof APIError ? res.error.originalError.message : "Request failed"
        return { status: "unknown", message }
      }
      const fresh = res.data.teams.find((t) => t.team_public_id === teamPublicId)
      return fresh ? { status: "still-unbacked", team: fresh } : { status: "backed" }
    },
    [refetch]
  )

  // Quiet while loading: a spinner here would compete with the page's own
  // skeleton for the table below, and a moment of absence reads correctly.
  if (isLoading) return null

  // NOT quiet on error. This panel's whole premise is that silence means
  // all-clear, so a failed check that renders as nothing tells the admin the
  // opposite of the truth — "no teams are drifting" when we simply do not know.
  // Teams one webhook away from losing access is exactly the wrong thing to be
  // wrong about quietly.
  if (error) {
    return (
      <div className="bg-surface rounded-lg border border-red-300 p-4 flex items-start gap-3">
        <ExclamationTriangleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div>
          <h2 className="font-semibold text-primary">Could not check for unbacked access</h2>
          <p className="text-sm text-secondary mt-0.5">
            {error instanceof APIError ? error.originalError.message : "Request failed"}. This is not
            an all-clear — teams may be holding access with no billing row behind it. Reload to try
            again.
          </p>
        </div>
      </div>
    )
  }

  const teams = data?.teams ?? []
  if (teams.length === 0) return null

  return (
    <div className="bg-surface rounded-lg border border-yellow-300">
      <div className="flex items-start gap-3 p-4 border-b border-border">
        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
        <div>
          <h2 className="font-semibold text-primary">
            Access with no billing row ({teams.length})
          </h2>
          <p className="text-sm text-secondary mt-0.5">
            These teams have active access that no live subscription backs, so they are missing from
            the table below. A team whose only subscription is cancelled is one webhook away from
            losing access — comping it creates the Stripe subscription that holds the grant in place.
          </p>
        </div>
      </div>
      <div className="px-4">
        {teams.map((team) => (
          <UnbackedTeamRow key={team.team_public_id} team={team} revalidate={revalidate} />
        ))}
      </div>
    </div>
  )
}
